/**
 * `HoldModal` (CDMX-18412) -- the presentational Hold-this-case form. A Chakra `Modal` that
 * collects a single-select reason / optional note / reminder (disabled this
 * phase, see the REMIND ME section below), validates ON SUBMIT (never by disabling the submit
 * buttons for validation reasons), shows the "Logged as" line, and calls `onSubmit(payload,
 * {loadNext})`. This component owns FORM state ONLY -- no api/context/clock; the container
 * (`HoldAction`) owns open/close wiring, the actual `placeHold` call, submit status, and the
 * `loggedAs` identity/clock values it passes in as props.
 *
 * "Hold & load next" is DISABLED this phase (a separate reason from validation): `loadNextCase`
 * isn't wired -- advancing needs the worklist cursor the URL-gated entry can't carry yet (deferred
 * to a CDMX-18412 follow-up). It renders visible-but-disabled with a
 * popover (`HOLD_NEXT_DISABLED_MSG`); "Hold & stay on this case" is the working path. The
 * `loadNextError` partial-success footer below is consequently unreachable until load-next lands.
 *
 * `buildHoldPayload` (`../../slots/buildHoldPayload`) is the single source of truth for
 * validation, reused for both submit buttons and the per-field error flags rendered below.
 *
 * Field errors (`hold-reason-error`, `hold-custom-error`) are LIVE-DERIVED every render from
 * `buildHoldPayload(values, submittedAt)` against the CURRENT field values -- so editing a field
 * after a failed submit clears its error immediately, no second submit needed. `submittedAt` is
 * captured once per submit click (never `new Date()` inline in render), so nothing here flickers
 * from the clock ticking on an unrelated re-render. A failed submit also focuses the first invalid
 * control, in order: reason group -> custom-reminder date. (The note is always optional now, so it
 * has no error; every reason is a concrete backend hold with no "describe" free-text.)
 *
 * `hold-submit-error` (footer) is a SEPARATE surface from the field errors above: an ASYNC failure
 * from the container's `onSubmit` (`submitState==='error'|'loadNextError'`/`errorMessage`). It's
 * hidden whenever a live field error is present, so a stale async banner can never linger over a
 * fresh validation error.
 *
 * `ModalBody` holds ONLY the subtitle: Chakra auto-wires the dialog's `aria-describedby` to
 * `ModalBody`'s id, so the actual REASON/NOTE/REMIND-ME form renders in a sibling `Box`
 * instead -- otherwise the whole form would become the dialog's accessible description.
 *
 * `submitState==='loadNextError'` is a DISTINCT partial-success state: `placeHold` succeeded but
 * `loadNextCase` then failed. The hold is already saved, so this component FREEZES every input
 * (`frozen`) and swaps the footer to just "Stay on this case" / "Retry loading next", which
 * retries ONLY the navigation (skips `buildHoldPayload` -- the already-accepted payload can't
 * change on a frozen form; see `attempt()`).
 *
 * Submit-button spinners are scoped to whichever button initiated the pending call (`intent:
 * 'stay' | 'next' | null`), so clicking one button's action never spins the other.
 */
import {
  Box,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Textarea,
  useRadio,
  useRadioGroup
} from '@chakra-ui/react';
import type { UseRadioProps } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

import type {
  HoldPayload,
  HoldReminderKey
} from '../../interface/hold';
import {
  buildHoldPayload,
  type HoldFormValues,
  type HoldValidationError
} from '../../slots/buildHoldPayload';
import { formatLoggedAtShort } from '../../slots/formatLoggedAt';
import { splitReasonOptions, toSelectedReasons } from '../../slots/splitReasonOptions';
import { CASE_ACTION_RADIUS, HOLD_COLORS } from '../../styles/tokens';
import { RoundedButton } from '@codametrix/ui-components';
import { ReasonField, useReasonOptions } from '../ReasonField';

/**
 * The design-system button ATOM (`@codametrix/ui-components`, boundary-allowlisted). `RoundedButton`
 * maps a spectre `className` (`btn-primary` / `btn-secondary` / `btn-clear` ...) to the theme's
 * button variant, so the footer renders the PLATFORM's standard buttons instead of bespoke Chakra
 * styling. Aliased with `isLoading` added: RoundedButton forwards that Chakra spinner prop to the
 * underlying Chakra Button at runtime but doesn't list it on its native `ButtonHTMLAttributes` type.
 */
const AtomButton = RoundedButton as React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { isLoading?: boolean }
>;

export type HoldSubmitState =
  | 'idle'
  | 'placing'
  | 'loadingNext'
  | 'error'
  | 'loadNextError';

export interface HoldModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly loggedAs: {
    readonly name: string;
    readonly at: Date;
    /** IANA zone, e.g. 'America/New_York' -- pins the footer timestamp for deterministic rendering. */
    readonly timeZone?: string;
  };
  readonly onSubmit: (
    payload: HoldPayload,
    opts: { loadNext: boolean }
  ) => Promise<void>;
  readonly submitState: HoldSubmitState;
  readonly errorMessage?: string;
  /** Chakra restores focus here on close (the container passes its Hold trigger ref). */
  readonly finalFocusRef?: React.RefObject<HTMLElement>;
}

/** Data-driven "Remind me" options. No "None" pill (the section is optional and defaults to
 *  nothing selected); the group's sentinel `'none'` value (see `useRadioGroup` below) matches no
 *  rendered pill, so the default render has none checked. */
const REMINDER_OPTIONS: ReadonlyArray<{
  readonly value: HoldReminderKey;
  readonly label: string;
}> = [
  { value: 'in1Hour', label: 'In 1 hour' },
  { value: 'in4Hours', label: 'In 4 hours' },
  { value: 'tomorrowMorning', label: 'Tomorrow morning' },
  { value: 'in3Days', label: 'In 3 days' },
  { value: 'custom', label: 'Custom…' }
];

/** Shared eyebrow-label style (section/field headings) -- was copy-pasted 4x. */
// No `textTransform: 'uppercase'` here -- it would cascade into the child hint spans ("required" /
// "optional" / "select one" / "coming soon"), which stay normal case. The section word is written
// uppercase literally instead (REASON / NOTE / REMIND ME).
const EYEBROW = {
  textStyle: 'Subtitle 1',
  color: HOLD_COLORS.inkSoft
} as const;

/**
 * Thin alias over `slots/formatLoggedAt.ts`, which the case-action modals share.
 *
 * KEEP THE 2-ARG SHAPE: aliasing this to a `(at, style, timeZone)` function would land `timeZone` in the
 * `style` slot and format in the ambient zone — which on a machine set to America/New_York still satisfies
 * this file's own test.
 */
export const formatLoggedAs = (at: Date, timeZone?: string): string =>
  formatLoggedAtShort(at, timeZone);

interface RadioPillProps extends UseRadioProps {
  readonly label: string;
  readonly testId: string;
}

/**
 * One pill in a single-select radiogroup -- shared by the Reason group and the Remind-me group.
 * `useRadio` must run inside its own component (rules of hooks forbid calling it per-iteration
 * inside `HoldModal`'s `.map()`), so this renders the real, native, visually-hidden
 * `<input type="radio">` that `useRadio` builds (grouped by the shared `name` `useRadioGroup`
 * generates) plus a styled `<span>` for the visible pill. Native grouping gives roving-tabindex +
 * arrow-key move-and-select for free (APG Radio Group).
 *
 * The visible span uses `getLabelProps()`, not `getCheckboxProps()`: `getCheckboxProps()` sets
 * `aria-hidden` (correct for Chakra's own `Radio`, where the checkbox glyph and label text are
 * separate sibling spans), but this pill fuses "indicator" and "label" into one visual capsule --
 * putting the pill's text inside an `aria-hidden` span would leave the native input with no
 * accessible name at all. `getLabelProps()` keeps the text in the accessible-name computation.
 */
/** Dialog title -- the SAME string names the visible header and the dialog's `aria-label` accessible
 *  name (WCAG 2.5.3 Label-in-Name requires they match; one constant keeps them from drifting). */
const HOLD_MODAL_TITLE = 'Hold this case';

/**
 * "Hold & load next" is DISABLED for now. Advancing to the next case needs the worklist cursor
 * (`getPrevOrNextCase`), which the URL-gated starburst entry can't carry across the reload yet --
 * full wiring plan tracked as a CDMX-18412 follow-up. The button stays visible
 * but disabled, with a hover/focus popover carrying this copy so the gap is explicit (not a dead
 * click). "Hold & stay on this case" is the working path meanwhile.
 */
const HOLD_NEXT_DISABLED_MSG =
  'Loading the next case isn’t available yet. Your hold still saves — use “Hold & stay on this case.”';

const RadioPill: React.FC<RadioPillProps> = ({
  label,
  testId,
  ...radioProps
}) => {
  const { getInputProps, getLabelProps, state } = useRadio(radioProps);
  return (
    <Box
      as="label"
      data-testid={testId}
      display="inline-flex"
      cursor={state.isDisabled ? 'not-allowed' : 'pointer'}
    >
      <input {...getInputProps()} />
      <Box
        as="span"
        {...getLabelProps()}
        textStyle="Body 1"
        px="0.8125rem"
        py="0.4375rem"
        borderRadius="full"
        border="1px solid"
        borderColor={state.isChecked ? HOLD_COLORS.chipSelected : HOLD_COLORS.border}
        bg={state.isChecked ? HOLD_COLORS.chipSelected : 'white'}
        color={state.isChecked ? 'white' : HOLD_COLORS.inkSoft}
        opacity={state.isDisabled ? 0.5 : 1}
        boxShadow={state.isFocused ? 'outline' : undefined}
        // Fade the fill/border/text between checked states + a light press-in on click. Colors ease
        // over 160ms (the "fading"); `transform` is quicker (90ms) so the tap feels responsive. The
        // press-scale is suppressed on disabled pills (Remind-me "coming soon") -- they don't click.
        transition="background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 120ms ease, transform 90ms ease"
        _active={state.isDisabled ? undefined : { transform: 'scale(0.94)' }}
      >
        {label}
      </Box>
    </Box>
  );
};

export const HoldModal: React.FC<HoldModalProps> = ({
  isOpen,
  onClose,
  loggedAs,
  onSubmit,
  submitState,
  errorMessage,
  finalFocusRef
}) => {
  const [reasonKeys, setReasonKeys] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [caseContext, setCaseContext] = useState('');
  const [reminder, setReminder] = useState<HoldReminderKey | undefined>(
    undefined
  );
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('');
  // Timestamp of the last submit attempt -- default `null` so nothing renders as an error before
  // the user has ever submitted. Per-field errors below are derived fresh each render from
  // `buildHoldPayload(values, submittedAt)`, never snapshotted.
  const [submittedAt, setSubmittedAt] = useState<Date | null>(null);
  // Which button initiated the in-flight submit -- scopes the spinner to that one button.
  const [intent, setIntent] = useState<'stay' | 'next' | null>(null);

  // Focus targets, in first-invalid-wins order: reason group -> custom reminder date. Reason is the
  // only always-present required field; the custom date is required only when the custom pill is on.
  // The reason group is a `fieldset`, not natively focusable, hence `tabIndex={-1}` below
  // (programmatically focusable, not in the Tab order).
  const reasonGroupRef = useRef<HTMLElement>(null);
  const customDateRef = useRef<HTMLInputElement>(null);
  // The payload from the last SUCCESSFUL `buildHoldPayload` call -- read only by the
  // `loadNextError` retry path in `attempt()`, which resubmits it without re-validating a frozen
  // form. Never rendered, so a ref (not state) is correct here.
  const placedPayloadRef = useRef<HoldPayload | null>(null);

  // Reset ALL form state on each closed->open transition (design §4a) -- no stale reasons/note/errors
  // survive a reopen. `HoldAction` also remounts this component fresh per open (`key={openGen}`);
  // this effect is defense-in-depth for any caller that doesn't remount.
  useEffect(() => {
    if (isOpen) {
      setReasonKeys([]);
      setNote('');
      setCaseContext('');
      setReminder(undefined);
      setCustomDate('');
      setCustomTime('');
      setSubmittedAt(null);
      setIntent(null);
    }
  }, [isOpen]);

  const { state: reasonState, retry } = useReasonOptions('hold', isOpen);
  const options = reasonState.status === 'loaded' ? reasonState.options : [];
  const selected = toSelectedReasons(options, reasonKeys)[0];
  const noReasons =
    reasonState.status !== 'loaded' ||
    splitReasonOptions(options).pills.length + splitReasonOptions(options).listOptions.length === 0;

  const busy = submitState === 'placing' || submitState === 'loadingNext';
  // `frozen` disables every INPUT during an in-flight submit or after a partial success
  // (loadNextError); `busy` keeps inputs frozen through a retry's transient `loadingNext` too.
  // NOTE: the FOOTER layout keys on `submitState === 'loadNextError'` directly (below), so during a
  // real load-next RETRY (submitState briefly 'loadingNext') it momentarily shows the normal
  // 3-button set. Invisible today (stubbed loadNextCase resolves synchronously); tighten when the
  // real adapter lands (PT-1) by latching a "saw loadNextError" flag for the footer.
  const frozen = busy || submitState === 'loadNextError';
  const customReminderAt =
    customDate && customTime ? `${customDate}T${customTime}` : '';
  const values: HoldFormValues = {
    reason: selected,
    note,
    caseContext,
    reminder,
    customReminderAt
  };

  // Per-field validation flags for display -- live derivation: recomputed from CURRENT `values`
  // every render, against the FIXED `submittedAt` timestamp (never `new Date()` here). Named
  // `liveResult`/`errs`, distinct from `attempt()`'s own local `result` below, to avoid shadowing.
  const liveResult = submittedAt ? buildHoldPayload(values, submittedAt) : null;
  const errs: HoldValidationError[] =
    liveResult && liveResult.ok === false ? liveResult.errors : [];
  const reasonError = errs.includes('reason-required');
  const customError = errs.some((e) => e.startsWith('custom-reminder'));

  const chooseReminder = (k?: HoldReminderKey): void => {
    setReminder(k);
    if (k !== 'custom') {
      setCustomDate('');
      setCustomTime('');
    }
  };
  const { getRadioProps } = useRadioGroup({
    value: reminder ?? 'none',
    onChange: (next) =>
      chooseReminder(next === 'none' ? undefined : (next as HoldReminderKey))
  });

  // Focus the FIRST invalid control after a failed attempt, in reason -> custom-date order.
  // `custom-reminder-*` fire only when `values.reminder === 'custom'` (custom date rendered), so the
  // ref is never null for a reachable branch.
  const focusFirstInvalid = (errors: HoldValidationError[]): void => {
    if (errors.includes('reason-required')) {
      reasonGroupRef.current?.focus();
    } else if (errors.some((e) => e.startsWith('custom-reminder'))) {
      customDateRef.current?.focus();
    }
  };

  const attempt = async (loadNext: boolean): Promise<void> => {
    setIntent(loadNext ? 'next' : 'stay');
    if (submitState === 'loadNextError') {
      // Partial success already happened (placeHold succeeded, loadNextCase didn't). The form is
      // frozen and its values can't have changed, so this is purely a retry of the load-next
      // navigation -- skip re-validating and resubmit the payload that was already accepted. If
      // nothing was ever placed (unreachable today, load-next disabled) and the current form isn't
      // even valid, there is nothing to resubmit -- the retry is a no-op. HoldAction's `placedRef`
      // guard skips `placeHold` again and retries only `loadNextCase`.
      const fallback = buildHoldPayload(values, new Date());
      const retryPayload =
        placedPayloadRef.current ?? (fallback.ok === true ? fallback.payload : undefined);
      if (!retryPayload) return;
      await onSubmit(retryPayload, { loadNext: true });
      return;
    }
    const now = new Date();
    setSubmittedAt(now);
    const result = buildHoldPayload(values, now);
    // Computed ONCE and reused for both the focus-first-invalid call and the payload, so what's
    // focused after a failed attempt always matches what actually blocked it.
    // `result.ok === false` (not `!result.ok`) is deliberate: with this repo's `strict: false`
    // tsconfig, narrowing this discriminated union via `!result.ok` does NOT narrow `result`
    // (TS2339 on `.errors`), while the equality form narrows correctly.
    if (result.ok === false) {
      focusFirstInvalid(result.errors);
      return; // errors render below (validate-on-submit; buttons stay enabled)
    }
    placedPayloadRef.current = result.payload;
    await onSubmit(result.payload, { loadNext });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={busy ? () => undefined : onClose}
      isCentered
      size="xl"
      finalFocusRef={finalFocusRef}
      closeOnOverlayClick={!busy}
      closeOnEsc={!busy}
    >
      <ModalOverlay />
      <ModalContent
        data-testid="hold-modal"
        borderRadius={CASE_ACTION_RADIUS.modal}
        maxW="52rem"
        aria-label={HOLD_MODAL_TITLE}
      >
        {/* Header: the pause icon sits on the LEFT, centered against the stacked title + subtitle.
            It's a FIXED 44px square (`boxSize`), not a stretched box: `aspect-ratio` is ignored on
            a flex item whose width would otherwise collapse to its 12px min-content (the two
            bars), rendering it tall-and-narrow -- an explicit square is the reliable fix. The
            dialog's accessible NAME is pinned to "Hold this case" via `aria-label` on ModalContent:
            unlike `aria-labelledby`, `aria-label` is not part of Chakra's `getDialogProps` spread,
            so it survives and the visible subtitle never pollutes the name. The subtitle lives in
            a padding-stripped ModalBody so Chakra still wires it as the accessible DESCRIPTION
            (`aria-describedby`). The icon is aria-hidden. */}
        <Flex
          align="center"
          gap={3}
          px={7}
          pt={6}
          pb={5}
          borderBottomWidth="1px"
          borderColor={HOLD_COLORS.border}
        >
          <Box
            aria-hidden
            data-testid="hold-modal-icon"
            bg={HOLD_COLORS.iconBg}
            borderRadius={CASE_ACTION_RADIUS.tile}
            boxSize="2.75rem"
            flexShrink={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="0.25rem"
          >
            <Box w="0.25rem" h="0.875rem" bg={HOLD_COLORS.iconBar} borderRadius="1px" />
            <Box w="0.25rem" h="0.875rem" bg={HOLD_COLORS.iconBar} borderRadius="1px" />
          </Box>
          <Flex direction="column" justify="center" gap="0.125rem" minW={0}>
            <Text textStyle="h3">
              {HOLD_MODAL_TITLE}
            </Text>
            <ModalBody p={0}>
              <Text
                textStyle="Body 1"
                color={HOLD_COLORS.inkMuted}
              >
                This case will be put on hold and can be found in your Holds tab.
              </Text>
            </ModalBody>
          </Flex>
        </Flex>
        {/* Vertically centers the 24px close button on the 44px pause icon: icon center = header
            pt(24px) + 44/2 = 46px from ModalContent's top, so top = 46 - 24/2 = 34px (overrides
            Chakra's default ~2px). `right={7}` mirrors the header's `px={7}` so the button's right
            inset equals the pause icon's left inset -- Chakra's default (~12px) sat closer to the edge. */}
        <ModalCloseButton
          isDisabled={busy}
          aria-label="Close"
          top="2.125rem"
          right={7}
          bg={HOLD_COLORS.inputBg}
          border={`1px solid ${HOLD_COLORS.border}`}
          borderRadius={CASE_ACTION_RADIUS.small}
          size="sm"
          color={HOLD_COLORS.inkMuted}
          _hover={{ bg: HOLD_COLORS.closeHoverBg }}
        />
        <Box px={7} pt={5} pb={5} maxH="60vh" overflowY="auto">
          {/* REASON -- single-select dictionary reason (ReasonField) */}
          <Box
            as="fieldset"
            role="radiogroup"
            ref={reasonGroupRef}
            tabIndex={-1}
            aria-label="Reason, required, select one"
            aria-required="true"
            aria-invalid={reasonError || undefined}
            aria-describedby={reasonError ? 'hold-reason-error' : undefined}
            mb={6}
          >
            {/* `display="block"` overrides the browser UA `legend { display: table }`: a table box
                wraps this label's inline content in anonymous table-cells and strips the whitespace
                text node between the "required"/"select one" spans, rendering them run-together
                (the space IS in the DOM -- only the table layout drops it). Block layout keeps it. */}
            <Text as="legend" {...EYEBROW} display="block" mb={3}>
              REASON{' '}
              <Text as="span" color={HOLD_COLORS.error}>required</Text>{' '}
              <Text as="span" color={HOLD_COLORS.inkFaint} fontWeight={400}>
                select one
              </Text>
            </Text>
            <ReasonField
              kind="hold"
              idPrefix="hold"
              mode="single"
              state={reasonState}
              onRetry={retry}
              selectedKeys={reasonKeys}
              onChange={setReasonKeys}
              isDisabled={frozen}
              palette={{
                chipSelected: HOLD_COLORS.chipSelected,
                border: HOLD_COLORS.border,
                inkSoft: HOLD_COLORS.inkSoft,
                inkMuted: HOLD_COLORS.inkFaint,
                error: HOLD_COLORS.error
              }}
            />
            {reasonError && (
              <Text
                data-testid="hold-reason-error"
                id="hold-reason-error"
                role="alert"
                color={HOLD_COLORS.error}
                textStyle="Body 1"
                mt={1}
              >
                You must select a hold reason.
              </Text>
            )}
          </Box>

          {/* NOTE */}
          <Box mb={6}>
            <Text as="label" htmlFor="hold-note-input" {...EYEBROW}>
              NOTE{' '}
              <Text as="span" color={HOLD_COLORS.inkFaint} fontWeight={400}>
                optional
              </Text>
            </Text>
            <Textarea
              id="hold-note-input"
              data-testid="hold-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              isDisabled={frozen}
              maxLength={1024}
              placeholder="Add a note (optional)…"
              bg={HOLD_COLORS.inputBg}
              borderColor={HOLD_COLORS.border}
              borderRadius={CASE_ACTION_RADIUS.control}
              textStyle="Body 1"
              px="0.875rem"
              py="0.6875rem"
              _hover={{ borderColor: HOLD_COLORS.noteHoverBorder }}
              _placeholder={{ color: HOLD_COLORS.inkFaint }}
              mt={1}
            />
          </Box>

          {/* ADD CONTEXT FOR THIS CASE */}
          <Box mb={6}>
            <Text as="label" htmlFor="hold-context-input" {...EYEBROW}>
              ADD CONTEXT FOR THIS CASE{' '}
              <Text as="span" color={HOLD_COLORS.inkFaint} fontWeight={400}>
                optional
              </Text>
            </Text>
            <Textarea
              id="hold-context-input"
              data-testid="hold-context"
              value={caseContext}
              onChange={(e) => setCaseContext(e.target.value)}
              isDisabled={frozen}
              maxLength={2000}
              placeholder="Add context for this case (optional)…"
              bg={HOLD_COLORS.inputBg}
              borderColor={HOLD_COLORS.border}
              borderRadius={CASE_ACTION_RADIUS.control}
              textStyle="Body 1"
              px="0.875rem"
              py="0.6875rem"
              _placeholder={{ color: HOLD_COLORS.inkFaint }}
              mt={1}
            />
          </Box>

          {/* REMIND ME -- disabled for Path A ("coming soon"); no backend scheduling exists yet
              (parity PT-3). Kept visible as a signpost -- the radiogroup + custom date/time block
              stay wired so re-enabling is a one-line change -- but every pill is force-disabled so
              `reminder` never leaves `undefined` and no reminder is ever submitted. */}
          <Box role="radiogroup" aria-label="Remind me, coming soon" aria-disabled>
            <Text {...EYEBROW} mb={3}>
              REMIND ME{' '}
              <Text as="span" color={HOLD_COLORS.inkFaint} fontWeight={400}>
                coming soon
              </Text>
            </Text>
            <Box bg={HOLD_COLORS.inputBg} border={`1px solid ${HOLD_COLORS.border}`} borderRadius={CASE_ACTION_RADIUS.control} p={3}>
              <Flex wrap="wrap" gap={3}>
                {REMINDER_OPTIONS.map(({ value, label }) => (
                  <RadioPill
                    key={value}
                    testId={`hold-reminder-${value}`}
                    label={label}
                    {...getRadioProps({ value, isDisabled: true })}
                  />
                ))}
              </Flex>
            </Box>
            {reminder === 'custom' && (
              <Flex gap={2} mt={2}>
                <Input
                  type="date"
                  ref={customDateRef}
                  data-testid="hold-custom-date"
                  aria-label="Custom reminder date"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  isDisabled={frozen}
                  aria-invalid={customError || undefined}
                  aria-describedby={customError ? 'hold-custom-error' : undefined}
                  bg={HOLD_COLORS.inputBg}
                  borderColor={HOLD_COLORS.border}
                  borderRadius={CASE_ACTION_RADIUS.control}
                  textStyle="Body 1"
                />
                <Input
                  type="time"
                  data-testid="hold-custom-time"
                  aria-label="Custom reminder time"
                  value={customTime}
                  onChange={(e) => setCustomTime(e.target.value)}
                  isDisabled={frozen}
                  aria-invalid={customError || undefined}
                  aria-describedby={customError ? 'hold-custom-error' : undefined}
                  bg={HOLD_COLORS.inputBg}
                  borderColor={HOLD_COLORS.border}
                  borderRadius={CASE_ACTION_RADIUS.control}
                  textStyle="Body 1"
                />
              </Flex>
            )}
            {customError && (
              <Text
                data-testid="hold-custom-error"
                id="hold-custom-error"
                role="alert"
                color={HOLD_COLORS.error}
                textStyle="Body 1"
                mt={1}
              >
                Choose a future date and time.
              </Text>
            )}
          </Box>
        </Box>
        <ModalFooter
          alignItems="center"
          gap={4}
          px={7}
          // Symmetric padding via `sx` -- Chakra's ModalFooter baseStyle overrides a bare `pt` prop
          // (top≈4.6px vs bottom 20px, content sits ~7px high). Equal top/bottom centers the
          // "Logged as" + buttons in the footer bar.
          sx={{ paddingTop: '1.125rem', paddingBottom: '1.125rem' }}
          borderTopWidth="1px"
          borderColor={HOLD_COLORS.border}
        >
          <Text
            data-testid="hold-logged-as"
            textStyle="Body 1"
            color={HOLD_COLORS.inkSoft}
            flex="0 1 auto"
            flexShrink={1}
          >
            Logged as <Text as="b" color={HOLD_COLORS.ink}>{loggedAs.name}</Text> ·{' '}
            {formatLoggedAs(loggedAs.at, loggedAs.timeZone)}
          </Text>
          <Flex flex="1" gap={3} align="center" justify="flex-end" flexWrap="wrap">
            {(submitState === 'error' || submitState === 'loadNextError') &&
              errorMessage &&
              errs.length === 0 && (
                <Text
                  data-testid="hold-submit-error"
                  role="alert"
                  color={HOLD_COLORS.error}
                  textStyle="Body 1"
                >
                  {errorMessage}
                </Text>
              )}
            {submitState === 'loadNextError' ? (
              // Partial-success footer -- the hold is already saved, so this replaces the normal
              // Cancel/Stay/Load-next trio with exactly two actions. Test ids stay stable
              // (`hold-submit-stay`/`hold-submit-next`) even though the labels/handlers differ.
              <>
                <AtomButton
                  className="btn-secondary"
                  data-testid="hold-submit-stay"
                  onClick={onClose}
                >
                  Stay on this case
                </AtomButton>
                <AtomButton
                  className="btn-primary"
                  data-testid="hold-submit-next"
                  onClick={() => attempt(true)}
                  isLoading={busy && intent === 'next'}
                >
                  Retry loading next
                </AtomButton>
              </>
            ) : (
              <>
                <AtomButton
                  className="btn-secondary"
                  data-testid="hold-cancel"
                  onClick={onClose}
                  disabled={busy}
                >
                  Cancel
                </AtomButton>
                <AtomButton
                  className="btn-secondary"
                  data-testid="hold-submit-stay"
                  onClick={() => attempt(false)}
                  isLoading={busy && intent === 'stay'}
                  disabled={frozen || noReasons}
                >
                  Hold &amp; stay on this case
                </AtomButton>
                {/* "Hold & load next" is DISABLED this phase (loadNextCase not wired -- see
                    HOLD_NEXT_DISABLED_MSG). A disabled <button> emits no hover/focus events, so the
                    explanatory popover hangs off a focusable wrapper span while the button itself is
                    pointer-inert + out of the tab order -- the hint stays reachable by mouse AND
                    keyboard. `attempt(true)` is therefore unreachable from the normal footer. */}
                <Popover trigger="hover" placement="top" openDelay={120} isLazy>
                  <PopoverTrigger>
                    <Box
                      as="span"
                      data-testid="hold-next-disabled"
                      // The disabled button is pointer-inert + tabIndex -1, so THIS span is the
                      // focusable popover trigger -- give it its own accessible name so a screen
                      // reader announces what the (unavailable) control is, not a nameless popup.
                      aria-label="Hold & load next (unavailable)"
                      display="inline-flex"
                      tabIndex={0}
                      borderRadius={CASE_ACTION_RADIUS.control}
                      _focusVisible={{ boxShadow: 'outline' }}
                    >
                      <AtomButton
                        className="btn-primary"
                        data-testid="hold-submit-next"
                        disabled
                        tabIndex={-1}
                        style={{ pointerEvents: 'none' }}
                      >
                        Hold &amp; load next →
                      </AtomButton>
                    </Box>
                  </PopoverTrigger>
                  <PopoverContent
                    maxW="16.25rem"
                    bg={HOLD_COLORS.ink}
                    color="white"
                    border="none"
                    borderRadius={CASE_ACTION_RADIUS.control}
                    textStyle="Body 1"
                    lineHeight="1.45"
                    _focus={{ outline: 'none' }}
                  >
                    <PopoverArrow bg={HOLD_COLORS.ink} />
                    <PopoverBody data-testid="hold-next-disabled-popover">
                      {HOLD_NEXT_DISABLED_MSG}
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </>
            )}
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
